import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

// Common office-bearer designations for Indian trusts/societies, plus a
// "Custom..." escape hatch for anything not on the list.
const DESIGNATIONS = [
  'Chairman', 'President', 'Vice President', 'General Secretary', 'Joint Secretary',
  'Secretary', 'Treasurer', 'Managing Trustee', 'Trustee', 'Founder', 'Executive Member', 'Advisor',
];

const EMPTY_FORM = { name: '', designation: '', bio: '', order: 0 };

export default function ManageManagement() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [designationChoice, setDesignationChoice] = useState('');
  const [file, setFile] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchMembers = () => {
    setLoading(true);
    API.get('/management/admin').then(r => { setMembers(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchMembers(); }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setDesignationChoice('');
    setFile(null);
    setEditingId(null);
  };

  const openAddForm = () => { resetForm(); setShowForm(true); };

  const openEditForm = (m) => {
    setEditingId(m._id);
    setForm({ name: m.name, designation: m.designation, bio: m.bio || '', order: m.order || 0 });
    setDesignationChoice(DESIGNATIONS.includes(m.designation) ? m.designation : 'Custom');
    setFile(null);
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); resetForm(); };

  const handleDesignationSelect = (value) => {
    setDesignationChoice(value);
    // Switching to a preset overwrites whatever was typed in the custom
    // field; switching to Custom clears a stale preset value instead of
    // silently submitting it.
    if (value !== 'Custom') setForm(p => ({ ...p, designation: value }));
    else setForm(p => ({ ...p, designation: DESIGNATIONS.includes(p.designation) ? '' : p.designation }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.designation.trim()) return toast.error('Designation is required');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('image', file);
      if (editingId) {
        await API.put(`/management/${editingId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Member updated');
      } else {
        await API.post('/management', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Management member added');
      }
      closeForm();
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving member');
    }
    setSaving(false);
  };

  const handleTogglePublish = async (m) => {
    try {
      await API.put(`/management/${m._id}`, { isActive: !m.isActive });
      toast.success(m.isActive ? 'Moved to draft' : 'Published');
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this member? This cannot be undone.')) return;
    try {
      await API.delete(`/management/${id}`);
      toast.success('Member deleted');
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting member');
    }
  };

  const getUrl = m => m?.image ? `${API_BASE}${m.image}` : null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 className="admin-page-title" style={{ margin: 0 }}>🙏 Manage Management</h2>
        <button className="btn btn-primary" onClick={() => (showForm ? closeForm() : openAddForm())}>
          {showForm ? 'Cancel' : '+ Add Member'}
        </button>
      </div>

      {showForm && (
        <div className="admin-form-card" style={{ marginBottom: 24 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>{editingId ? 'Edit Management Member' : 'Add Management Member'}</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input className="form-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Designation *</label>
              <select className="form-select" value={designationChoice} onChange={e => handleDesignationSelect(e.target.value)}>
                <option value="">Select designation...</option>
                {DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                <option value="Custom">Custom...</option>
              </select>
              {designationChoice === 'Custom' && (
                <input
                  className="form-input" style={{ marginTop: 8 }} placeholder="Enter designation"
                  value={form.designation} onChange={e => setForm(p => ({ ...p, designation: e.target.value }))}
                />
              )}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Bio (Optional)</label>
            <textarea className="form-input" rows={2} value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Photo{editingId ? ' (leave empty to keep current)' : ''}</label>
              <input type="file" accept="image/*" onChange={e => setFile(e.target.files[0])} />
            </div>
            <div className="form-group">
              <label className="form-label">Display Order</label>
              <input className="form-input" type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update Member' : 'Save Member'}
            </button>
            <button className="btn btn-outline" onClick={closeForm}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="loader"><div className="spinner" /></div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {members.map(m => (
            <div key={m._id} style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', position: 'relative', textAlign: 'center', padding: '20px 14px 14px' }}>
              <span className={`badge ${m.isActive ? 'badge-green' : 'badge-orange'}`} style={{ position: 'absolute', top: 10, left: 10 }}>
                {m.isActive ? 'Published' : 'Draft'}
              </span>
              {getUrl(m)
                ? <img src={getUrl(m)} alt={m.name} style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', margin: '20px auto 10px', display: 'block', opacity: m.isActive ? 1 : 0.55 }} />
                : <div style={{ width: 80, height: 80, borderRadius: '50%', margin: '20px auto 10px', background: 'var(--saffron-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', opacity: m.isActive ? 1 : 0.55 }}>👤</div>
              }
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{m.name}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--maroon)', fontWeight: 600, marginTop: 2, marginBottom: 14 }}>{m.designation}</div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                <button title="View" onClick={() => setViewing(m)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>👁</button>
                <button title="Edit" onClick={() => openEditForm(m)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>✏️</button>
                <button title={m.isActive ? 'Move to Draft' : 'Publish'} onClick={() => handleTogglePublish(m)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>
                  {m.isActive ? '📦' : '📢'}
                </button>
                <button title="Delete" onClick={() => handleDelete(m._id)} style={{ background: 'none', border: '1px solid #f5c6c6', color: '#c62828', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>🗑</button>
              </div>
            </div>
          ))}
          {members.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 40, color: '#888' }}>No management members added yet.</div>
          )}
        </div>
      )}

      {viewing && (
        <div className="modal-overlay" onClick={() => setViewing(null)}>
          <div className="modal-card auth-card" style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setViewing(null)} aria-label="Close">✕</button>
            {getUrl(viewing)
              ? <img src={getUrl(viewing)} alt={viewing.name} style={{ width: 110, height: 110, borderRadius: '50%', objectFit: 'cover', margin: '0 auto 14px' }} />
              : <div style={{ width: 110, height: 110, borderRadius: '50%', margin: '0 auto 14px', background: 'var(--saffron-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem' }}>👤</div>
            }
            <h3 style={{ fontWeight: 700 }}>{viewing.name}</h3>
            <div style={{ color: 'var(--maroon)', fontWeight: 600, marginTop: 4 }}>{viewing.designation}</div>
            <span className={`badge ${viewing.isActive ? 'badge-green' : 'badge-orange'}`} style={{ display: 'inline-block', marginTop: 10 }}>
              {viewing.isActive ? 'Published' : 'Draft'}
            </span>
            {viewing.bio && <p style={{ marginTop: 16, color: 'var(--text-mid)', fontSize: '0.9rem', lineHeight: 1.6, textAlign: 'left' }}>{viewing.bio}</p>}
            <div style={{ marginTop: 16, fontSize: '0.8rem', color: '#999' }}>Display order: {viewing.order ?? 0}</div>
          </div>
        </div>
      )}
    </div>
  );
}
